import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { User } from "../../../redux/user";
import { TestResultForm, TestResultFormField, WaterlineInventoryResponse, WaterlineItem, WaterlinePermissions, WaterlineTestCompliance, WaterlineTestResult, WaterlineUserPermissions } from "./types";
import { ComplianceStatus, TeamMember } from "../../../types";
import { UserAccessLevels } from '../../../enums';

export const formatStandardDate = (date: string | undefined | null | Date) => {
  if (!date) return '';

  const cleanDate = typeof date === 'string' ? date.replace(/Z$/, '') : date;

  return dayjs(cleanDate).format('MM/DD/YYYY');
};

export const formatUtcDate = (utcDate: string | undefined | null | Date) => {
  if (!utcDate) return '';

  dayjs.extend(utc);
  dayjs.extend(localizedFormat);

  return dayjs.utc(utcDate).local().format('MM/DD/YYYY');
};

export const getReplacementDate = (date: string | undefined) => {
  if (!date) return '';

  return dayjs(date).add(1, 'year').format('MM/DD/YYYY');
};

export const mapWaterlineInventoryToItem = (
  item: WaterlineInventoryResponse,
  customerNumber?: string
): WaterlineItem => {
  const nonKitCustomerNumber = customerNumber ?? item.customer_number;

  return {
    id: item.waterline_inventory_id,
    customer_number: nonKitCustomerNumber,
    nonkit_item_id: item.nonkit_item_id,
    room_id: item.room_id,
    active: item.waterline_active,
    status: item.waterline_status,
    activated_date: item.activated_date,
    created_date: '',
    name: item.item_name,
    subcategory: item.item_subcategory,
    item_number: item.item_number,
    shipped_date: item.shipped_date,
    invoice_number: item.invoice_number,
    is_tbr: item.is_tbr,
    room:
      item.room_id && item.room_name
        ? {
            room_id: item.room_id,
            customer_number: nonKitCustomerNumber,
            name: item.room_name,
            description: item.room_description ?? '',
            created_date: '',
            deleted_date: item.room_deleted_date ?? ''
          }
        : undefined
  };
};

export const getUserPermission = (roles: UserAccessLevels[], user: User | null) => {
  const userRole = user?.access_level
  if (!userRole) return false
  const hasPermission = roles.some(role => role === userRole)
  return hasPermission
}

export const getWaterlinePermissions = () => {
  const permissions: WaterlinePermissions = {
    inOffice: {
      add: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      edit: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      delete: [UserAccessLevels.Owner, UserAccessLevels.Admin],
    },
    roomChair: {
      add: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      assign: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      edit: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      delete: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
      view: [UserAccessLevels.Owner, UserAccessLevels.Admin, UserAccessLevels.TeamMember],
    },
  }

  return permissions
}

export const getWaterlineUserPermissions = (user: User | null, permissions: WaterlinePermissions) => {
  const userPermissions: WaterlineUserPermissions = {
    inOffice: {
      add: getUserPermission(permissions.inOffice.add, user),
      edit: getUserPermission(permissions.inOffice.edit, user),
      delete: getUserPermission(permissions.inOffice.delete, user),
    },
    roomChair: {
      add: getUserPermission(permissions.roomChair.add, user),
      assign: getUserPermission(permissions.roomChair.assign, user),
      edit: getUserPermission(permissions.roomChair.edit, user),
      delete: getUserPermission(permissions.roomChair.delete, user),
      view: getUserPermission(permissions.roomChair.view, user),
    },
  }

  return userPermissions
}

export const groupTestsBy = (displayResultsBy: string, testResults: WaterlineTestResult[]) => {
  const base: Map<string, WaterlineTestResult[]> = new Map()

  const groups = testResults.reduce((grouped, result) => {
    const key = displayResultsBy === 'roomChair'
    ? result.room?.name ?? ''
    : result.test_date ?? ''

    grouped.get(key)?.push(result) ?? grouped.set(key, [result])

    return grouped
  }, base)

  return groups
}

export const sortGroupKeysBy = (displayResultsBy: string, groups: Map<string, WaterlineTestResult[]>) => {
  const keys = Array.from(groups.keys())

  const callback = displayResultsBy === 'roomChair'
    ? (a: string, b: string) => a.localeCompare(b)
    : (a: string, b: string) => new Date(b).getTime() - new Date(a).getTime()

  keys.sort(callback)

  return keys
}

export const getWaterlineTestCompliance = (testResults: WaterlineTestResult[]) => {
  const compliance: WaterlineTestCompliance = { status: 0, message: '' }

  const base = {
    passed: 0,
    retestPassed: 0,
    failed: 0,
  }

  const counts = testResults.reduce((counts, result) => {
    if (result.is_pass && result.retest) {
      ++counts.retestPassed
    } else if (result.is_pass) {
      ++counts.passed
    } else {
      ++counts.failed
    }
    
    return counts
  }, base)

  if (counts.passed) {
    compliance.message = 'Test Passed'
    compliance.status = ComplianceStatus.Passed
  } else if (counts.retestPassed) {
    compliance.message = 'Retest Passed'
    compliance.status = ComplianceStatus.RetestPassed
  } else {
    compliance.message = 'Test Failed'
    compliance.status = ComplianceStatus.Failed
  }

  return compliance
}

export const getTeamMembersIndex = (teamMembers: TeamMember[]) => {
  const teamMembersIndex: Record<string, TeamMember> = {}

  teamMembers.forEach(teamMember => {
    const userId = teamMember.login_user_id
    teamMembersIndex[userId] = teamMember
  })

  return teamMembersIndex
}

export const getTestResultFormData = (formData: TestResultForm, testResult: WaterlineTestResult) => {
  const fields = Object.keys(formData) as TestResultFormField[]

  const entries = fields.map(field => {
    if (field === 'test_date' || field === 'report_date') {
      return [field, dayjs(testResult[field]).utc().format('MM/DD/YYYY')]
    }

    const value = testResult[field]

    return [field, value]
  })

  const form: TestResultForm = Object.fromEntries(entries)

  return form
}
